import { Dialog } from "@dash/dialog";
import { Button } from "@dash/form";
import { SectionHeader } from "@dash/page";
import { Tile, TileStripe } from "@dash/tile";
import { css, useTheme } from "@emotion/react";
import { useState } from "react";

import { PackageItem } from "@megaron/fulfillment-contracts";

type Props = {
  items: PackageItem[];
  orderItems: PackageItem[];
  activeId: number | null;
  onAddItem: (item: PackageItem) => void;
  getDisplayName: (variantId: string, fallbackName: string) => string;
};

export const ToPackList = ({ items, orderItems, activeId, onAddItem, getDisplayName }: Props) => {
  const theme = useTheme();
  const [isDialogOpen, setDialogOpen] = useState(false);

  const handleAdd = (item: PackageItem) => {
    onAddItem({ ...item, quantity: 1 });
    setDialogOpen(false);
  };

  const hasAvailableItems = items.some((i) => i.quantity > 0);

  const sortedItems = [...items].sort((a, b) => {
    const fullA = orderItems.find((i) => i.variantId === a.variantId);
    const fullB = orderItems.find((i) => i.variantId === b.variantId);
    const packedA = (fullA ? fullA.quantity : 0) - a.quantity;
    const packedB = (fullB ? fullB.quantity : 0) - b.quantity;
    const isCompleteA = packedA === (fullA?.quantity ?? 0);
    const isCompleteB = packedB === (fullB?.quantity ?? 0);
    if (isCompleteA !== isCompleteB) return Number(isCompleteA) - Number(isCompleteB);
    if (!isCompleteA && !isCompleteB) return Number(b.weight ?? 0) - Number(a.weight ?? 0);
    return 0;
  });

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <SectionHeader isHr>Do zapakowania</SectionHeader>

      {sortedItems.map((item) => {
        const full = orderItems.find((i) => i.variantId === item.variantId);
        const packed = full ? full.quantity - item.quantity : 0;
        const isComplete = packed === (full?.quantity ?? 0);

        return (
          <Tile
            key={item.variantId}
            gap="8px"
            stripe={<TileStripe color={isComplete ? theme.colors.primaryLight : theme.colors.primary} />}
          >
            <div
              css={css({
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "1rem",
                fontWeight: 600,
              })}
            >
              <span>{getDisplayName(item.variantId, item.name)}</span>
              <span>
                {packed} / {full?.quantity ?? 0} szt.
              </span>
            </div>
          </Tile>
        );
      })}

      <Button
        variant="outline"
        size="small"
        css={{ marginLeft: "auto" }}
        onClick={() => setDialogOpen(true)}
        isDisabled={activeId === null || !hasAvailableItems}
      >
        Dodaj produkt
      </Button>

      {isDialogOpen && (
        <Dialog header="Dodaj produkt do paczki" onClose={() => setDialogOpen(false)} css={{ maxWidth: 480 }}>
          <div css={{ display: "flex", flexDirection: "column", gap: "12px", padding: "1rem 0" }}>
            {items
              .filter((i) => i.quantity > 0)
              .sort((a, b) => Number(b.weight ?? 0) - Number(a.weight ?? 0))
              .map((item) => (
                <Tile
                  key={item.variantId}
                  gap="8px"
                  stripe={<TileStripe color={theme.colors.primary} />}
                  css={{ cursor: "pointer" }}
                  onClick={() => handleAdd(item)}
                >
                  <div css={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>{getDisplayName(item.variantId, item.name)}</span>
                    <span>{item.quantity} szt.</span>
                  </div>
                </Tile>
              ))}
          </div>
        </Dialog>
      )}
    </div>
  );
};
